import React, { Fragment, FC, useState, useEffect, useRef } from 'react'
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from 'react-router-dom'
import { Amplify, Hub, Auth } from 'aws-amplify'
import { CssBaseline } from '@mui/material'
import { useConfig } from './config'
import RedirectPage from 'components/redirect'
import ShowingChoice from 'components/showingchoice'
import Loader from 'components/loader/loader'
import Container from 'components/container/container'
import { Logout, Denied } from './pages'
import './app.css'
import { constants } from './utils/constants'
import { FlagsmithProvider } from 'flagsmith/react'
import flagsmith from 'flagsmith'
import { FeatureFlagGuard } from './components/FeatureFlagGuard'
import { getAuthReturnRoute, withSessionRecovery } from './utils/auth/session'
interface IProps {
  content?: JSX.Element
}

const App: FC<IProps> = () => {
  const [user, setUser] = useState<any>(null)
  const [userMemberKey, setUserMemberKey] = useState('')
  const { config, configLoading, configError } = useConfig()
  const [authRedirect, setAuthRedirect] = useState(true)
  const [authChecked, setAuthChecked] = useState(false)
  const authReturnRoute = useRef('/')

  const resetAuthState = () => {
    localStorage.removeItem('AuthState')
    localStorage.removeItem(constants.TOKEN_FIELD)
    setUser(null)
  }

  const checkAndUpdateAuthState = async () => {
    try {
      const userData = await Auth.currentAuthenticatedUser()
      setUser(userData)
      setUserMemberKey(
        userData.signInUserSession?.idToken?.payload?.MemberKey || '',
      )
      localStorage.setItem(
        constants.TOKEN_FIELD,
        userData.signInUserSession?.idToken?.jwtToken,
      )
      localStorage.setItem('AuthState', 'authenticated')
      setAuthChecked(true)
    } catch (error) {
      resetAuthState()
      setAuthChecked(true)
      localStorage.setItem('redirectAfterAuth', authReturnRoute.current)
      localStorage.setItem('AuthState', 'pending')
      if (config?.idpProvider) {
        Auth.federatedSignIn({ customProvider: config.idpProvider })
      }
    }
  }

  useEffect(() => {
    if (config) {
      // Amplify removes callback parameters before reporting success/failure.
      // Capture the launch now so a failed callback cannot replace it with '/'.
      authReturnRoute.current = getAuthReturnRoute()
      Amplify.configure(withSessionRecovery(config.amplifyConfig))

      checkAndUpdateAuthState()

      const unsubscribe = Hub.listen('auth', ({ payload: { event } }) => {
        switch (event) {
          case 'signIn':
          case 'cognitoHostedUI': {
            const targetRoute = localStorage.getItem('redirectAfterAuth')
            if (targetRoute) {
              window.history.replaceState({}, document.title, targetRoute)
              localStorage.removeItem('redirectAfterAuth')
            }
            localStorage.setItem('AuthState', 'authenticated')
            checkAndUpdateAuthState()
            break
          }
          case 'signOut':
            resetAuthState()
            break
          case 'signIn_failure':
          case 'cognitoHostedUI_failure':
          case 'customState_failure':
            resetAuthState()
            break
          default:
            break
        }
      })

      return () => unsubscribe()
    }
  }, [config])

  useEffect(() => {
    if (config && authChecked && !user) {
      const searchParams = new URLSearchParams(window.location.search)
      const isAuthCallback = searchParams.has('code')
      const authState = localStorage.getItem('AuthState')

      if (
        !isAuthCallback &&
        authState !== 'pending' &&
        authState !== 'authenticated'
      ) {
        localStorage.setItem('redirectAfterAuth', authReturnRoute.current)
        localStorage.setItem('AuthState', 'pending')
        if (config.idpProvider) {
          Auth.federatedSignIn({ customProvider: config.idpProvider })
        }
      }
    }
  }, [config, authChecked, user])

  useEffect(() => {
    if (config && authChecked) {
      const searchParams = new URLSearchParams(window.location.search)
      const isAuthCallback = searchParams.has('code')
      const authState = localStorage.getItem('AuthState')
      if (
        config.authByButtonAction &&
        authState !== 'authenticated' &&
        authState !== 'pending' &&
        authRedirect &&
        !isAuthCallback &&
        !user
      ) {
        localStorage.setItem('redirectAfterAuth', authReturnRoute.current)
        Auth.federatedSignIn({ customProvider: config.idpProvider })
          .then(() => {
            setAuthRedirect(false)
          })
          .catch(() => {
            resetAuthState()
          })
      }
    }
  }, [config, authRedirect, authChecked, user])

  if (configLoading) {
    return (
      <Container>
        <Loader visible={true} />
      </Container>
    )
  }
  if (configError) return <div>{configError}</div>
  if (!config) {
    return <div>Config not loaded.</div>
  }
  if (!user) {
    return (
      <Container>
        <Loader visible={true} />
      </Container>
    )
  }
  return (
    <FlagsmithProvider
      flagsmith={flagsmith}
      options={{
        environmentID: config.FLAGSMITH_ENV_APIKEY,
      }}
    >
      <Router>
        <Fragment>
          <CssBaseline />
          <Routes>
            <Route
              path="/"
              element={<Navigate replace to="/showingchoice" />}
            />
            <Route path="/redirect" element={<RedirectPage />} />
            <Route
              path="/showingchoice"
              element={
                <FeatureFlagGuard userData={user}>
                  <ShowingChoice memberKey={userMemberKey} />
                </FeatureFlagGuard>
              }
            />
            <Route path="/logout" element={<Logout />} />
            <Route path="/denied" element={<Denied />} />
          </Routes>
        </Fragment>
      </Router>
    </FlagsmithProvider>
  )
}

export default App
